import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useId, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import type {DownloadedFile} from "../api/response";
import type {RevaThunkAction} from "../redux/revaHooks";
import {useRevaDispatch} from "../redux/revaHooks";
import {openBlobForDownload} from "../util/browserUtil";

interface AvmDocumentationLinkProps {
    /** Thunk-Factory, die das PDF über den REVA-Proxy holt (siehe `redux/pdfThunk`). */
    fetchDocument: FetchDocument;
    /** Beschriftung des Links. */
    label: string;
    /** Tooltip-Text. */
    tooltip: string;
    /** Dateiname, falls die AVM-API keinen via `Content-Disposition` mitschickt. */
    fallbackFilename: string;
    /** Zusätzliche Klassen für den Link. */
    className?: string;
}

/** Thunk-Factory, die ein Doku-PDF über den REVA-Proxy holt (siehe `redux/pdfThunk`). */
export type FetchDocument = () => RevaThunkAction<Promise<DownloadedFile>>;

/**
 * Download eines Doku-PDFs samt Fehler-Toast, geteilt von {@link AvmDocumentationLink} und dem
 * Dokumentations-Menü in der Fußzeile (#19302). Das zurückgegebene `toast` muss gerendert werden.
 * @returns `download(fetchDocument, fallbackFilename)`, ob gerade geladen wird, und das Toast-Element
 */
export function useDocumentationDownload() {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const [downloading, setDownloading] = useState(false);
    const toastRef = useRef<Toast>(null);

    const download = async (fetchDocument: FetchDocument, fallbackFilename: string) => {
        setDownloading(true);
        try {
            const {blob, filename} = await dispatch(fetchDocument());
            openBlobForDownload(blob, filename ?? fallbackFilename);
        } catch (e) {
            console.error("Failed to download AVM documentation", e);
            toastRef.current?.show({
                severity: "error",
                summary: "Fehler",
                detail: t("value.info.documentationDownloadError"),
                life: 5000,
            });
        } finally {
            setDownloading(false);
        }
    };

    const toast = <Toast ref={toastRef} position="top-center" />;
    return {download, downloading, toast};
}

/**
 * Link, der ein AVM-Doku-PDF herunterlädt (#19432).
 *
 * Bewusst kein `<a href>` auf die AVM-API: der Browser würde dabei den Authorization-Header der
 * SPA nicht mitschicken und der Nutzer landete in einem Basic-Auth-Dialog. Stattdessen holt der
 * Klick das PDF per fetch mit Credentials über den REVA-eigenen `/avm-api`-Proxy und reicht den
 * Blob an den Browser-Download weiter.
 *
 * @param props siehe {@link AvmDocumentationLinkProps}
 * @returns der Download-Link
 */
export function AvmDocumentationLink({
    fetchDocument,
    label,
    tooltip,
    fallbackFilename,
    className = "",
}: AvmDocumentationLinkProps) {
    const {download, downloading, toast} = useDocumentationDownload();
    // Tooltip/Link über eine instanz-eigene Klasse verbinden: die Links kommen mehrfach pro Seite
    // vor (Info-Panel + Fußzeile). Eine geteilte id wäre ungültiges HTML, ein geteilter
    // Klassen-Selektor würde jeden Button an alle gerenderten Tooltips hängen. Die Doppelpunkte
    // aus useId() müssen raus, sonst ist der Selektor kein gültiger Klassenname.
    const tooltipId = useId().replaceAll(":", "");
    const tooltipClass = `avmDocLink-${tooltipId}`;

    return (
        <>
            {/* [font:inherit]: Preflight ist aus, sonst bekommt der Button die kleinere Browser-Formularschrift statt der des umgebenden Textes. */}
            <button
                type="button"
                className={`${tooltipClass} [font:inherit] p-0 bg-transparent border-none no-underline mx-4 text-primary cursor-pointer ${className}`}
                disabled={downloading}
                onClick={() => void download(fetchDocument, fallbackFilename)}
                data-pr-position="top"
                data-pr-tooltip={tooltip}
            >
                {label}
            </button>
            <Tooltip target={`.${tooltipClass}`} className="custom-tooltip" />
            {toast}
        </>
    );
}
